import React, { useRef, useState } from 'react';
import { AddedToCartModal } from 'javascripts/components/deals/added_to_cart_modal';
import { FullShoppingCartModal } from 'javascripts/components/full_shopping_cart_modal';

import { graphQL } from 'javascripts/shared/graphql';
import { bubEvents } from 'javascripts/shared/events/bub_events';
import { graphQLMutations } from 'javascripts/shared/graphql/mutations';
import { siteLinkData } from 'javascripts/shared/utils/site_link_data';

import { googleTagManagerUtils } from 'javascripts/shared/utils/google_tag_manager_utils';
import { userData } from 'javascripts/shared/utils/user_data';
import { urlUtils } from 'javascripts/shared/utils/url_utils';

const bookStates = {
  NEITHER_CART_NOR_WISHLIST: { inWishlist: false, inCart: false },
  CART_ONLY: { inWishlist: false, inCart: true },
  WISHLIST_ONLY: { inWishlist: true, inCart: false },
};

const fullCartRegex = /You cannot add more than \d+ audiobooks to your cart./;

export function useShoppingCartAndWishlist() {
  const audiobook = useRef({ id: null, coverUrl: '', currentProduct: { purchaseUrl: '' } });
  const [fullShoppingCartModalOpen, setFullShoppingCartModalOpen] = useState(false);
  const [addedToCartModalOpen, setAddedToCartModalOpen] = useState(false);

  const [addedToWishlistInsteadOfCart, setAddedToWishlistInsteadOfCart] = useState(false);

  const shoppingCartCountElement = useRef(document.getElementById('header-shopping-cart-number'));

  function incrementShoppingCartCounter() {
    if (_.isNil(shoppingCartCountElement.current)) {
      return;
    }

    shoppingCartCountElement.current.dispatchEvent(new CustomEvent('increment'));
  }

  function actuallyAddItemToWishlist(book, { inCart = false, fromFullCart = false } = {}) {
    const mutation = inCart
      ? graphQLMutations.moveItemFromShoppingCartToWishlist
      : graphQLMutations.addItemToCurrentUserWishlist;

    return graphQL
      .get(mutation, {
        audiobookId: book.id,
      })
      .then(() => {
        if (fromFullCart) {
          bubEvents.track('Full Shopping Cart Modal', 'Added to Wishlist', {
            label: 'Audiobook Id',
            value: book.id,
          });
        }

        if (window.gtag !== undefined) {
          gtag('event', 'add_to_wishlist', { event_label: book.displayTitle });
        }

        return Object.assign(bookStates.WISHLIST_ONLY, { audiobookId: book.id });
      })
      .catch(() => {
        if (!fromFullCart) {
          window.alert('Sorry, we were unable to perform this action. Please refresh and try again.'); // eslint-disable-line no-alert
        }
        return bookStates.NEITHER_CART_NOR_WISHLIST;
      });
  }

  function redirectLoggedOutWishlister(book) {
    urlUtils.setPath(siteLinkData.getPath('signUpWithWishlistItem', book.id));
  }

  function addItemToWishlist(book, { inCart = false, fromFullCart = false } = {}) {
    if (userData.isLoggedIn()) {
      return actuallyAddItemToWishlist(book, { inCart, fromFullCart });
    }
    return userData.getSession().then(session => {
      if (session.userId) {
        return actuallyAddItemToWishlist(book, { inCart, fromFullCart });
      }
      if (!fromFullCart) {
        redirectLoggedOutWishlister(book);
      }
      return {
        inCart,
        inWishlist: false,
        audiobookId: book.id,
      };
    });
  }

  function removeItemFromWishlist(book) {
    return graphQL
      .get(graphQLMutations.removeItemFromCurrentUserWishlist, { audiobookId: book.id })
      .then(() => {
        return bookStates.NEITHER_CART_NOR_WISHLIST;
      })
      .catch(() => {
        window.alert('Sorry, we were unable to perform this action. Please refresh and try again.'); // eslint-disable-line no-alert
        return bookStates.WISHLIST_ONLY;
      });
  }

  function removeItemFromCart(book) {
    return graphQL
      .get(graphQLMutations.deleteCurrentUserShoppingCartItem, { audiobookId: book.id })
      .then(() => {
        if (window.gtag !== undefined) {
          gtag('event', 'remove_from_cart', { event_label: book.displayTitle });
        }
        return Object.assign(bookStates.NEITHER_CART_NOR_WISHLIST, { audiobookId: book.id });
      })
      .catch(() => {
        window.alert('Sorry, we were unable to perform this action. Please refresh and try again.'); // eslint-disable-line no-alert
        return bookStates.CART_ONLY;
      });
  }

  function addItemToCart(
    book,
    {
      inWishlist = false,
      shouldRenderAddedToCartModal = true,
      moveToWishlistOnFullCart = true,
      suppressErrorMessage = false,
    } = {},
  ) {
    audiobook.current = book;

    const mutation = inWishlist
      ? graphQLMutations.moveItemFromWishlistToShoppingCart
      : graphQLMutations.addItemToCurrentUserShoppingCart;

    return graphQL
      .get(mutation, { audiobookId: book.id })
      .then(response => {
        incrementShoppingCartCounter();
        if (shouldRenderAddedToCartModal) {
          setAddedToCartModalOpen(true);
        }

        const fbEventId = inWishlist
          ? response.moveWishlistItemToShoppingCart?.shoppingCartItem?.fbEventId
          : response.createShoppingCartItem?.shoppingCartItem?.fbEventId;

        googleTagManagerUtils.pushAddToCartData(book.id, book.currentProduct.discountPrice, fbEventId);

        return bookStates.CART_ONLY;
      })
      .catch(error => {
        if (_.get(error, 'response.errors[0].message', '').match(fullCartRegex)) {
          if (!suppressErrorMessage) {
            setFullShoppingCartModalOpen(true);
          }
          if (moveToWishlistOnFullCart) {
            return addItemToWishlist(book, { inCart: false, fromFullCart: true, redirectIfLoggedOut: false }).then(
              data => {
                setAddedToWishlistInsteadOfCart(data.inWishlist);
                return data;
              },
            );
          }
        } else if (!suppressErrorMessage) {
          window.alert('Sorry, we were unable to add this book to your cart. Please refresh and try again.'); // eslint-disable-line no-alert
        }
        return bookStates.NEITHER_CART_NOR_WISHLIST;
      });
  }

  function onFullCartModalClose() {
    setFullShoppingCartModalOpen(false);
    setAddedToWishlistInsteadOfCart(false);
  }

  function renderFullShoppingCartModal() {
    return (
      <FullShoppingCartModal
        isOpen={fullShoppingCartModalOpen}
        audiobook={audiobook.current}
        onRequestClose={onFullCartModalClose}
        showWishlistMessage={addedToWishlistInsteadOfCart}
      />
    );
  }

  function renderAddedToCartModal() {
    return (
      <AddedToCartModal
        audiobook={audiobook.current}
        isOpen={addedToCartModalOpen}
        onRequestClose={() => setAddedToCartModalOpen(false)}
      />
    );
  }

  return {
    cartActions: {
      addItemToCart,
      removeItemFromCart,
      addItemToWishlist,
      removeItemFromWishlist,
    },
    renderFullShoppingCartModal,
    renderAddedToCartModal,
  };
}
